/**
 * useBackButton — LIFO stack hook for Telegram WebApp BackButton.
 *
 * Contract:
 *   - Register a handler via `useBackButton(handler, deps?)`.
 *   - On mount: pushes handler onto the module-level stack, shows BackButton,
 *     and (idempotently, once) wires the global `BackButton.onClick` listener.
 *   - On unmount: pops handler; if stack is empty AND no overlay is open,
 *     hides BackButton.
 *
 * Priority order (highest first):
 *   1. Top overlay onClose handler (from OverlayContext.getTopOverlayClose).
 *   2. Top route handler on the LIFO stack.
 *
 * Usage:
 *   useBackButton(() => navigate('/'), [navigate])
 *
 * AppShell mounts a default `navigate(-1)` handler so all child routes
 * are covered automatically. A route that needs custom behaviour (e.g.
 * navigate('/')) simply calls `useBackButton` itself — it stacks on top
 * and wins until that route unmounts.
 */

import { useContext, useEffect } from 'react'
import { OverlayContext } from '../components/useOverlay'

// Module-level LIFO stack — intentionally outside React so it survives
// hot-reloads and remains a single shared instance across all hook calls.
const _handlerStack: Array<() => void> = []

// Guard: register the global BackButton.onClick callback only once.
let _registered = false

function getBackButton(): TelegramBackButton | undefined {
  return window.Telegram?.WebApp?.BackButton
}

function syncVisibility(hasOverlay: boolean): void {
  const bb = getBackButton()
  if (!bb) return
  try {
    if (_handlerStack.length > 0 || hasOverlay) {
      bb.show()
    } else {
      bb.hide()
    }
  } catch {
    /* no-op */
  }
}

function ensureRegistered(getTopOverlayClose: () => (() => void) | null): void {
  if (_registered) return
  _registered = true

  const bb = getBackButton()
  if (!bb) return

  try {
    bb.onClick(() => {
      // Overlay takes precedence over route handlers.
      const overlayClose = getTopOverlayClose()
      if (overlayClose !== null) {
        overlayClose()
        return
      }
      const top = _handlerStack[_handlerStack.length - 1]
      if (top !== undefined) {
        top()
      }
    })
  } catch {
    /* no-op */
  }
}

export function useBackButton(handler: () => void, deps?: unknown[]): void {
  const overlayCtx = useContext(OverlayContext)

  // Capture a stable ref to getTopOverlayClose so the module-level listener
  // always reads the latest overlay state without re-registering.
  const getTopOverlayClose = overlayCtx?.getTopOverlayClose ?? ((): null => null)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    ensureRegistered(getTopOverlayClose)

    _handlerStack.push(handler)
    syncVisibility(getTopOverlayClose() !== null)

    return () => {
      // Pop the handler we pushed (remove last occurrence to be safe).
      const idx = _handlerStack.lastIndexOf(handler)
      if (idx !== -1) {
        _handlerStack.splice(idx, 1)
      }
      syncVisibility(getTopOverlayClose() !== null)
    }
    // deps spread is intentional — caller controls re-registration exactly
    // like useEffect's own deps array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps ?? [handler])
}
